import axiosInstance from "../utils/axiosConfig";
import { useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import Button from "../Input/Button";
import Modal from "@material-ui/core/Modal";
import { makeStyles } from "@material-ui/core/styles";

export default function SelectAvatarModal(props) {
    const { user, theme } = props;
    
    const useStyles = makeStyles((theme2) => ({
        button: {
                width: "80px",
                height: "25px",
                borderRadius: "12px",
                fontSize: "10px",
                margin: "5px"
            },
            root: {
                minHeight: 500,
                minWidth: 258
            },
            bullet: {
                minHeight: "100px",
                margin: "0 2px",
                transform: "scale(0.8)"
            },
            title: {
                fontSize: 32,
                fontFamily: "GoodUnicorn",
                color: theme.primaryColor
            },
            margin: {
                margin: theme2.spacing(1)
            },
            modal: {
                backgroundColor: theme2.palette.background.paper,
                border: "2px solid #000",
                boxShadow: theme2.shadows[5],
                padding: theme2.spacing(2, 4, 3),
                position: "absolute",
                width: 700,
                maxHeight: 600,
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                overflow: "auto"
            },
            selectedAvatar: {
                border: `3px solid ${theme.primaryColor}`, //#eb4c8a',
                borderRadius: "50%"
            },
            deselectedAvatar: {
                border: "3px solid #FFF"
            },
            divider: {
                margin: 10
            }
    }));
    
    const [selectedAvatar, setSelectedAvatar] = useState(user?.avatar?.id ? user.avatar.id : user.avatar_id);
    const [avatars, setAvatars] = useState([]);
    
    useEffect(() => {
        const fetchAvatars = async () => {
            try {
                const response = await axiosInstance.get(`/avatars`);
                console.log('SelectAvatarModal: Full avatar response:', response.data);
                
                // Filter avatars by gender
                let filteredAvatars = response.data.filter((item) => item.gender === user.gender);
                
                // For male users: Only show SVG avatars (exclude PNG boy avatars)
                // For female users: Keep all PNG avatars as they are
                if (user.gender === 'M') {
                    filteredAvatars = filteredAvatars.filter((item) => {
                        // Only include SVG files for boys (exclude PNG files)
                        return item.url.endsWith('.svg');
                    });
                    console.log('SelectAvatarModal: Filtered to SVG-only avatars for male user:', filteredAvatars);
                }
                
                console.log('SelectAvatarModal: Filtered avatars for gender', user.gender, ':', filteredAvatars);
                
                setAvatars(filteredAvatars);
                
                // If no avatars found for this gender, log warning
                if (filteredAvatars.length === 0) {
                    console.warn('SelectAvatarModal: No avatars found for gender:', user.gender);
                    console.warn('SelectAvatarModal: Available avatars:', response.data);
                }
            } catch (error) {
                console.error('SelectAvatarModal: Error fetching avatars:', error);
                // Set empty array to prevent undefined errors
                setAvatars([]);
            }
        };
        
        fetchAvatars();
    }, [user.gender]);
    
    const setAvatar = async () => {
        try {
            console.log('SelectAvatarModal: Updating avatar for user', user.id, 'to avatar ID', selectedAvatar);
            
            const response = await axiosInstance.post(`/user/${user.id}/avatar`, { 
                new_avatar_id: selectedAvatar 
            });
            
            console.log('SelectAvatarModal: Avatar update response:', response);
            
            if (response.status === 200) {
                console.log('SelectAvatarModal: Avatar updated successfully');
                console.log('SelectAvatarModal: Backend response data:', response.data);
                
                // CRITICAL FIX: Force fresh user data fetch on page reload to ensure React state consistency
                // This bypasses localStorage caching issues and ensures all components get fresh, accurate data
                try {
                    // Always force fresh fetch after avatar updates to prevent state inconsistencies
                    sessionStorage.setItem('FORCE_FRESH_USER_DATA', 'true');
                    console.log('SelectAvatarModal: Set FORCE_FRESH_USER_DATA flag to bypass cache on reload');
                    
                    // Optional: Update localStorage as backup, but primary solution is fresh fetch
                    const storedUser = window.localStorage.getItem('USER');
                    if (storedUser) {
                        const userData = JSON.parse(storedUser);
                        
                        // Update avatar_id field
                        if (response.data.avatar_id) {
                            userData.avatar_id = response.data.avatar_id;
                        }
                        
                        // Create complete avatar relationship object to match backend structure
                        if (response.data.avatar_url) {
                            userData.avatar = {
                                id: response.data.avatar_id,
                                url: response.data.avatar_url,
                                // Preserve existing properties
                                ...(userData.avatar || {}),
                            };
                            
                            console.log('SelectAvatarModal: Updated localStorage avatar object:', userData.avatar);
                        }
                        
                        // Store updated user data as backup
                        window.localStorage.setItem('USER', JSON.stringify(userData));
                    }
                } catch (error) {
                    console.error('SelectAvatarModal: Error in avatar update process:', error);
                    // Ensure fresh fetch happens even if localStorage update fails
                    sessionStorage.setItem('FORCE_FRESH_USER_DATA', 'true');
                }
                
                console.log('SelectAvatarModal: Reloading page to refresh all component states');
                window.location.reload();
            } else {
                console.error('SelectAvatarModal: Unexpected response status:', response.status);
            }
        } catch (error) {
            console.error('SelectAvatarModal: Error updating avatar:', error);
            // Show error to user instead of just failing silently
            alert('Failed to update avatar. Please try again.');
        }
    };
    
    const classes = useStyles();
    
    const modalBody = (
        <div className={classes.modal}>
            <Grid container spacing={2} justifyContent="center" style={{ padding: "20px" }}>
            {avatars.length === 0 ? (
                <Grid item xs={12} style={{ textAlign: "center", padding: "20px" }}>
                    <div>No avatars available for your gender.</div>
                    <div style={{ marginTop: "10px", fontSize: "12px", color: "#666" }}>
                        Please contact support if this issue persists.
                    </div>
                </Grid>
            ) : (
                avatars.map((avatar, index) => {
                    return (
                        <Grid key={index + 1 * Math.random()} item xs={3} style={{ display: "flex", justifyContent: "center" }}>
                            <img
                                key={index + 1 * Math.random()}
                                onClick={() => setSelectedAvatar(avatar.id)}
                                src={`/avatars/${avatar.url}`}
                                alt={`Avatar option ${index + 1}`}
                                className={
                                    avatar.id === selectedAvatar
                                    ? classes.selectedAvatar
                                    : classes.deselectedAvatar
                                }
                                style={{ width: "160px", height: "160px", cursor: "pointer" }}
                                onError={(e) => {
                                    console.error('SelectAvatarModal: Failed to load avatar image:', avatar.url);
                                    e.target.style.border = '2px solid red';
                                    e.target.alt = 'Failed to load';
                                }}
                            />
                        </Grid>
                    );
                })
            )}
            
            <Grid item xs={12} style={{ width: "100%", textAlign: "center", marginTop: "20px" }}>
                <Button 
                    theme={theme} 
                    onClick={setAvatar}
                    disabled={avatars.length === 0 || !selectedAvatar}
                >
                    Update
                </Button>
            </Grid>
            </Grid>
        </div>
    );
    
    return (
        <Modal
            open={props.open}
            onClose={props.handleClose}
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
        >
            {modalBody}
        </Modal>
    );
};